/**
 * Tools: operations of the IDE that a caller outside the page's own UI can
 * run by name with JSON arguments, such as an AI agent over MCP.
 *
 * A tool's definition is separate from its handler. The definitions are the
 * catalog in `catalog.ts`, which the relay lists to MCP clients whether or not
 * a tab is connected. Handlers need the page's runtime worker and device pane,
 * so the page adds them to a `ToolRegistry`.
 */

export type ArgType = "string" | "boolean" | "integer";

/** The JSON Schema of a tool's arguments: an object of named scalar values. */
export interface ArgsSchema {
    type: "object";
    properties: Record<string, { type: ArgType; description: string }>;
    required?: string[];
    additionalProperties: false;
}

export interface ToolDefinition {
    name: string;
    description: string;
    inputSchema: ArgsSchema;
}

/**
 * A definition with the TypeScript type of its arguments. `Args` must match
 * `inputSchema`; `ToolRegistry.call` checks the arguments against the schema
 * before the handler receives them.
 */
export interface Tool<Args> extends ToolDefinition {
    readonly args?: Args;
}

export function defineTool<Args>(definition: ToolDefinition): Tool<Args> {
    return definition;
}

/** What a call returns: text for the caller to read, and whether the call failed. */
export interface ToolResult {
    text: string;
    isError?: boolean;
}

export interface ToolContext {
    /** Report what a long call is doing now. */
    progress(message: string): void;
    /**
     * When the call before this one to the registry started, in `Date.now()`
     * time; the registry sets it, and leaves it out for the first call.
     */
    previousCall?: number;
}

export type ToolHandler<Args> = (args: Args, context: ToolContext) => Promise<ToolResult>;

export class ToolRegistry {
    readonly #handlers = new Map<string, { tool: ToolDefinition; handler: ToolHandler<never> }>();
    #lastCall: number | undefined;

    add<Args>(tool: Tool<Args>, handler: ToolHandler<Args>): void {
        if (this.#handlers.has(tool.name)) {
            throw new Error(`tool ${tool.name} is already registered`);
        }
        this.#handlers.set(tool.name, { tool, handler: handler as ToolHandler<never> });
    }

    /** Run a tool. A failure of any kind is returned as an error result. */
    async call(name: string, args: unknown, context: ToolContext): Promise<ToolResult> {
        const previousCall = this.#lastCall;
        this.#lastCall = Date.now();
        const entry = this.#handlers.get(name);
        if (entry === undefined) {
            return { text: `This idfweb tab has no tool ${name}.`, isError: true };
        }
        const problem = checkArgs(entry.tool.inputSchema, args ?? {});
        if (problem !== undefined) {
            return { text: `Invalid arguments for ${name}: ${problem}`, isError: true };
        }
        try {
            return await entry.handler(args as never, { ...context, previousCall });
        } catch (e) {
            return { text: e instanceof Error ? e.message : String(e), isError: true };
        }
    }
}

/** Why `args` does not match `schema`, or undefined when it does. */
export function checkArgs(schema: ArgsSchema, args: unknown): string | undefined {
    if (typeof args !== "object" || args === null || Array.isArray(args)) {
        return "expected an object";
    }
    const values = args as Record<string, unknown>;
    for (const name of schema.required ?? []) {
        if (values[name] === undefined) return `${name} is required`;
    }
    for (const [name, value] of Object.entries(values)) {
        const property = schema.properties[name];
        if (property === undefined) return `unknown argument ${name}`;
        if (value !== undefined && !hasType(value, property.type)) {
            return `${name} must be a ${property.type}`;
        }
    }
    return undefined;
}

function hasType(value: unknown, type: ArgType): boolean {
    return type === "integer" ? Number.isInteger(value) : typeof value === type;
}
